import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { ChevronRight, ChevronLeft, Sparkles, ArrowUpLeft, Trophy, Megaphone, Gift, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const AUTO_ROTATE_MS = 6000;

const ICONS = { sparkles: Sparkles, trophy: Trophy, megaphone: Megaphone, gift: Gift, shield: ShieldCheck };

function cardStyle(pos) {
  if (pos === 0) return { zIndex: 30, transform: 'translate(0px, 0px) scale(1)', opacity: 1, pointerEvents: 'auto' };
  if (pos === 1) return { zIndex: 20, transform: 'translate(10px, 10px) scale(0.94)', opacity: 0.55, pointerEvents: 'none' };
  if (pos === 2) return { zIndex: 10, transform: 'translate(20px, 20px) scale(0.88)', opacity: 0.28, pointerEvents: 'none' };
  return { zIndex: 0, transform: 'translate(20px, 20px) scale(0.85)', opacity: 0, pointerEvents: 'none' };
}

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('ar-SA', { day: 'numeric', month: 'short' }); } catch { return ''; }
};

export default function PlatformUpdatesCarousel({ className = '', onData }) {
  const [updates, setUpdates] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchRef = useRef({ startX: 0, active: false });

  useEffect(() => {
    axios.get(`${API}/api/platform/updates/public`).then((res) => {
      if (Array.isArray(res.data)) {
        setUpdates(res.data);
        if (onData) onData(res.data);
      }
    }).catch(() => {});
  }, [onData]);

  useEffect(() => {
    if (paused || updates.length <= 1) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % updates.length), AUTO_ROTATE_MS);
    return () => clearInterval(timer);
  }, [paused, updates.length]);

  const goNext = useCallback(() => setIndex((i) => (i + 1) % updates.length), [updates.length]);
  const goPrev = useCallback(() => setIndex((i) => (i - 1 + updates.length) % updates.length), [updates.length]);

  const onTouchStart = (e) => { touchRef.current = { startX: e.touches[0].clientX, active: true }; setPaused(true); };
  const onTouchEnd = (e) => {
    if (!touchRef.current.active) return;
    const diff = e.changedTouches[0].clientX - touchRef.current.startX;
    if (diff > 40) goPrev();
    else if (diff < -40) goNext();
    touchRef.current.active = false;
    setPaused(false);
  };

  if (updates.length === 0) return null;

  return (
    <div className={className} data-testid="platform-updates-carousel">
      <div className="mb-4 flex items-center justify-between gap-2 px-1">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-teal-400 shadow-[0_0_8px_2px_rgba(45,212,191,0.5)]" />
          <h3 className="text-sm font-bold tracking-wide text-white/90">آخر تحديثات تسيار</h3>
        </div>
        <a href="/updates" className="text-[11px] font-semibold text-teal-300 hover:text-teal-200" data-testid="platform-updates-view-all">عرض الكل ←</a>
      </div>

      <div
        className="relative h-[260px]"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="platform-updates-stack"
      >
        {updates.map((u, i) => {
          const pos = (i - index + updates.length) % updates.length;
          const style = cardStyle(pos);
          const CardIcon = ICONS[u.icon] || Sparkles;
          return (
            <div
              key={u.id}
              className="absolute inset-0 overflow-hidden rounded-2xl border border-white/25 bg-[#0f2438]/90 shadow-2xl backdrop-blur-xl transition-all duration-500 ease-out"
              style={style}
              data-testid={pos === 0 ? 'platform-update-active-card' : undefined}
            >
              <div className="flex h-full flex-col">
                {u.banner_image_url && (
                  <div className="relative h-24 w-full shrink-0" data-testid="platform-update-banner">
                    <img src={`${API}${u.banner_image_url}`} alt="" className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0f2438] via-transparent to-transparent" />
                  </div>
                )}
                <div className="flex flex-1 flex-col justify-between p-6 pt-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      {u.image_url ? (
                        <img src={`${API}${u.image_url}`} alt="" className="h-11 w-11 rounded-xl object-cover" />
                      ) : (
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-500/20 text-teal-300">
                          <CardIcon className="h-5 w-5" />
                        </div>
                      )}
                      <span className="rounded-full bg-orange-500/20 border border-orange-400/30 px-2.5 py-1 text-[11px] font-bold text-orange-300">
                        {u.badge || 'جديد'}
                      </span>
                    </div>
                    <span className="text-xs text-white/60">{fmtDate(u.publish_date)}</span>
                  </div>
                  <div className="min-w-0">
                    <h4 className="truncate text-lg font-bold text-white">{u.title}</h4>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-white/85">{u.description}</p>
                  </div>
                  {u.link && (
                    <a
                      href={u.link}
                      target={u.link.startsWith('/') ? undefined : '_blank'}
                      rel={u.link.startsWith('/') ? undefined : 'noopener noreferrer'}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-teal-300 hover:text-teal-200 transition-colors"
                      data-testid="platform-update-link"
                    >
                      اكتشف الميزة <ArrowUpLeft className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between px-1">
        <div className="flex items-center gap-1.5" data-testid="platform-updates-dots">
          {updates.map((u, i) => (
            <button
              key={u.id}
              onClick={() => setIndex(i)}
              aria-label={`عرض التحديث ${i + 1}`}
              data-testid={`platform-update-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${i === index ? 'w-6 bg-teal-400' : 'w-1.5 bg-white/25 hover:bg-white/40'}`}
            />
          ))}
        </div>
        {updates.length > 1 && (
          <div className="hidden items-center gap-1.5 sm:flex">
            <button onClick={goPrev} aria-label="التحديث السابق" data-testid="platform-update-prev-btn" className="rounded-full border border-white/15 p-2 text-white/60 hover:bg-white/10 hover:text-white transition-colors">
              <ChevronRight className="h-4 w-4" />
            </button>
            <button onClick={goNext} aria-label="التحديث التالي" data-testid="platform-update-next-btn" className="rounded-full border border-white/15 p-2 text-white/60 hover:bg-white/10 hover:text-white transition-colors">
              <ChevronLeft className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
